import { OptionalFormController, OptionalFormInclude, required, useGridForm } from "@itk/react-form";
import { useEffect, useMemo, useRef, useState } from "react";
import { AttributeRenderer, DekoxIcon } from "src/components";
import { useValidationMessageTranslationsT } from "src/translation-hooks/validation-message-translations";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { Model_AttributeDetailsGet } from "src/backend/openapi/v1";
import { configureProductActions } from "../../configure-product-store";
import { ConfigureProductSectionLoading } from "./configure-product-section-loading";
import { IconButton } from "@itk/mui-components";
import { genericTranslationsK } from "src/translation-hooks/generic-translations";
import { useParams } from "react-router";
import { LabelsMultiSelectChip } from "src/pages/configurator/components";
import { LabelsMultiSelectChipRef } from "src/pages/configurator/components/labels-multi-select-chip/labels-multi-select-chip-types";

const mapStateToProps = (state: RootState) => ({
  configurationState: state.pageReducer.configureProductReducer.configurationState,
  selectedProjectLineItemId: state.pageReducer.configureProductReducer.selectedProjectLineItemId
});

const ownActions = {
  updateProjectLineItemAttributeRequest: configureProductActions.updateProjectLineItemAttributeRequest,
  getGenerateConfigurationOrderRequest: configureProductActions.getGenerateConfigurationOrderRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ConfigureProductSectionBase = (props: Props) => {
  const { configurationState, selectedProjectLineItemId, updateProjectLineItemAttributeRequest, getGenerateConfigurationOrderRequest } = props;

  const { projectId = "", configurationId = "" } = useParams<{ projectId: string; configurationId: string }>();

  const validationMessageTranslationsT = useValidationMessageTranslationsT();

  const theme = useTheme();
  const labelsMultiChipRef = useRef<LabelsMultiSelectChipRef>(null);

  const [isModifiedLabels, setIsModifiedLabels] = useState(false);
  const [isLabelsLoading, setIsLabelsLoading] = useState(false);

  const selectedProjectLineItem = useMemo(() => {
    return configurationState.loading === "has-loaded" ? configurationState.data.projectLineItems.find(projectLineItem => projectLineItem.projectLineItemId === selectedProjectLineItemId) : undefined;
  }, [configurationState, selectedProjectLineItemId]);

  const initialData = useMemo(() => {
    if (configurationState.loading === "has-loaded") {
      return selectedProjectLineItem?.attributes || [];
    }
    return [];
  }, [selectedProjectLineItem]);

  const columns = useMemo(() => {
    if (selectedProjectLineItem?.attributes && selectedProjectLineItem?.attributes.length > 0) return Object.keys(selectedProjectLineItem.attributes[0]).map(key => ({ field: key }));
    return [];
  }, [selectedProjectLineItem]);

  const include = useMemo((): OptionalFormInclude<Model_AttributeDetailsGet> => {
    if (selectedProjectLineItem?.attributes && selectedProjectLineItem?.attributes.length > 0)
      return Object.keys(selectedProjectLineItem.attributes[0]).reduce(
        (acc, x) => {
          const key = x as keyof Model_AttributeDetailsGet;
          acc[key] = true;
          return acc;
        },
        {} as Record<keyof Model_AttributeDetailsGet, true>
      );
    return {};
  }, [selectedProjectLineItem]);

  const initController = useMemo(
    (): OptionalFormController<Model_AttributeDetailsGet> => ({
      value: {
        validators: data => {
          if (data.required.value) return [required];
          if (!data.isValid.value) return [_ => validationMessageTranslationsT.invalidValue];
          return [];
        }
      }
    }),
    []
  );

  const { rows, validate } = useGridForm<Model_AttributeDetailsGet, (typeof columns)[0], keyof Model_AttributeDetailsGet>(initialData, columns, "attributeId", initController, include);

  // useGridForm's final internal step runs silent validation — errors never reach form state.
  // Re-validate non-silently when isValid/required change so errors actually render.
  const validateRef = useRef(validate);
  validateRef.current = validate;

  const validationKey = useMemo(
    () => initialData.map(a => `${a.attributeId}:${String(a.isValid)}:${String(a.required)}`).join("|"),
    [initialData]
  );

  useEffect(() => {
    validateRef.current("all");
  }, [validationKey]);

  const handleChange = (attributeId: string | null, attributeValue: string | null) => {
    if (!attributeId || !selectedProjectLineItem?.projectLineItemId) return;

    updateProjectLineItemAttributeRequest({ attributeId, value: attributeValue?.toString() ?? "", projectLineItemId: selectedProjectLineItem.projectLineItemId });
    getGenerateConfigurationOrderRequest(projectId, configurationId);
  };

  const crossAndCheckBtnSx = {
    boxShadow: 3,
    borderRadius: 2,
    background: theme.palette.customColors.white,
    border: `1px solid ${theme.palette.customColors.border}`,
    color: theme.palette.success.main,
    position: "absolute",
    bottom: -34,
    right: 0,
    zIndex: 99,
    "&:hover, &.Mui-disabled": {
      background: theme.palette.customColors.white
    }
  };

  if (configurationState.loading === "is-loading") {
    return <ConfigureProductSectionLoading />;
  }

  if (configurationState.loading === "has-loaded") {
    return (
      <Stack gap={2} flex={1} overflow="hidden">
        <Typography px={2.4} pt={2} variant="body2" color={theme.palette.customColors.darkGrey}>
          {selectedProjectLineItem?.productDescription || ""} ({selectedProjectLineItem?.productNumber || ""})
        </Typography>
        <Stack gap={2} flex={1} overflow="auto" px={2.4} pb={4}>
          <Box position="relative">
            <LabelsMultiSelectChip
              key={selectedProjectLineItemId}
              ref={labelsMultiChipRef}
              projectId={projectId}
              entityIds={[selectedProjectLineItemId || ""]}
              onLoadingChange={setIsLabelsLoading}
              onChange={() => {
                setIsModifiedLabels(true);
              }}
            />

            {isModifiedLabels && (
              <>
                <IconButton
                  k={genericTranslationsK.done}
                  onClick={e => {
                    e.stopPropagation();
                    labelsMultiChipRef.current?.handleApply(() => {
                      setIsModifiedLabels(false);
                    });
                  }}
                  size="small"
                  loading={isLabelsLoading}
                  sx={{ ...crossAndCheckBtnSx, right: 34 }}
                >
                  <DekoxIcon icon="check" fontSize="small" />
                </IconButton>
                <IconButton
                  disabled={isLabelsLoading}
                  color="error"
                  k={genericTranslationsK.cancel}
                  onClick={e => {
                    e.stopPropagation();
                    setIsModifiedLabels(false);
                  }}
                  size="small"
                  sx={crossAndCheckBtnSx}
                >
                  <DekoxIcon icon="close" fontSize="small" color="error" />
                </IconButton>
              </>
            )}
          </Box>
          {rows.map(formItem => {
            return <AttributeRenderer key={formItem.formState.attributeId.value} formItem={formItem} handleChange={handleChange} />;
          })}
        </Stack>
      </Stack>
    );
  }

  return <></>;
};

export const ConfigureProductSection = connect(mapStateToProps, ownActions)(ConfigureProductSectionBase);
